import axios from "../../../../api/apiClient";
import { domain, token } from '../../../Security'; // ← adjust path to your config
import * as XLSX from 'xlsx';

import {
  fetchDataAccessTemplate,
  fetchDataAccounts,
  fetchAllEmployeeSingleData,
  fetchDataForClassification,
  fetchDataForClient,
  fetchDataForCompany,
  fetchDataForCostCenter,
  fetchDataForDepartment,
  fetchDataForEmployeeGroup1,
  fetchDataForEmployeeGroup2,
  fetchDataForEmployeeType,
  fetchDataForEmployment,
  fetchDataForJobTitle,
  fetchDataForLocation,
  fetchDataForPaytype,
  fetchDataForPlantilya,
  fetchDataForPosition,
  fetchDataForSection,
  fetchDataForShiftSchedule,
  fetchDataForVessel,
  fetchSalaryType,
  fetchDataForDestination
} from "../EmployeesListApi";

import { generateUniqueUsername } from '../utils/accountUtils';
import { convertToDateTime } from '../utils/textUtils';
import { FEATURES } from '../../../../config/features';
import leaveTypeService from "../../../../v2/services/leaveTypeService";
import { SALARYTYPES } from "../../../../utils/enums";
import { validateMscCompatibility } from "../../../../utils/validatorUtils";


// ==========================================
// Feature Flags
// ==========================================
const showTaxableStatus = !!FEATURES.EMPLOYEE_ACCOUNT_EMPLOYEE_BANK_TAXABLE_STAT;
const showSeamanBook = !!FEATURES.EMPLOYEE_ACCOUNT_SEAMAN_BOOK;
const showLicenseInfo = !!FEATURES.EMPLOYEE_ACCOUNT_LICENSE_INFORMATION;

export const createAccountBulk = async (bulkData, showGeneralMessage) => {

  // ── Step 1: Fetch prerequisites ──────────────────────────────────────────
  let accessTemplate, accounts, employeeInfo;

  try {
    [accessTemplate, accounts, employeeInfo] = await Promise.all([
      fetchDataAccessTemplate(),
      fetchDataAccounts(),
      fetchAllEmployeeSingleData(),
    ]);
  } catch (fetchError) {
    const msg = `Failed to load prerequisite data: ${fetchError.message}`;
    console.error('[createAccountBulk] Prerequisite fetch failed:', fetchError);
    showGeneralMessage(msg, false);
    throw fetchError;
  }

  // ── Step 2: Find template ────────────────────────────────────────────────
  const foundTemplate = accessTemplate.find(data =>
    data.description.toLowerCase() === ("Employee").toLowerCase()
  );

  if (!foundTemplate) {
    const msg = 'Access template "Employee" not found.';
    console.error('[createAccountBulk]', msg);
    showGeneralMessage(msg, false);
    throw new Error(msg);
  }

  // ── Step 3: Build payloads ───────────────────────────────────────────────
  const existingUsernames = []; // ← original: starts empty
  const dataToAdd = [];

  for (const employee of bulkData) {
    const foundEmployee = employeeInfo.find(data =>
      data.employeeId.toLowerCase() === employee.employeeId.toLowerCase()
    );

    // ← original behavior: pushes ALL account usernames on every iteration
    accounts.forEach(account => {
      existingUsernames.push(account.username);
    });

    const username = generateUniqueUsername(employee, existingUsernames);
    existingUsernames.push(username); // ← original: pushes again after generation

    dataToAdd.push({
      username: username,
      password: "password",
      employeePersonalInformationId: foundEmployee.id,
      accessTemplateId: foundTemplate.id
    });
  }

  // ── Step 4: POST to API ──────────────────────────────────────────────────
  try {
    await axios.post(
      `${domain}/api/Authentication/register/bulk`,
      dataToAdd,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (apiError) {
    let msg = 'Failed to register accounts.';
    try {
      const serverMsg = JSON.parse(
        apiError.response?.request?.responseText
      )?.message;
      if (serverMsg) msg = serverMsg;
    } catch {
      msg = apiError.message || msg;
    }

    console.error('[createAccountBulk] API error:', apiError);
    showGeneralMessage(msg, false);
    throw apiError;
  }
};

// Processing for Upload Employee/s Master List
export const processUploadEmployeeData = async ({ file, employees, options }) => {

  // 1. Fetch Reference Data
  const dataLocation = await fetchDataForLocation();
  const dataEmployment = await fetchDataForEmployment();
  const dataDepartment = await fetchDataForDepartment();
  const dataSection = await fetchDataForSection();
  const dataClassification = await fetchDataForClassification();
  const dataPaytype = await fetchDataForPaytype();
  const dataShiftSchedule = await fetchDataForShiftSchedule();
  const dataPosition = await fetchDataForPosition();
  const dataCompany = await fetchDataForCompany();
  const dataSalaryType = fetchSalaryType();
  const dataForMaintenanceSalaryCombination = options?.mscRules ?? [];
  const dataForJobTitle = await fetchDataForJobTitle();
  const dataForEmployeeGroup1 = await fetchDataForEmployeeGroup1();
  const dataForEmployeeGroup2 = await fetchDataForEmployeeGroup2();
  const dataForPlantilya = await fetchDataForPlantilya();
  const dataForCostCenter = await fetchDataForCostCenter();
  const dataForClient = await fetchDataForClient();
  const dataForEmployeeType = await fetchDataForEmployeeType();
  const dataForVessel = await fetchDataForVessel();
  const dataForDestination = await fetchDataForDestination();

  // New way, utilizing the V2
  const activeLeaveTypesRes = await leaveTypeService.getActive();
  const activeLeaveTypes = activeLeaveTypesRes.data || [];

  // 2. Helper Functions (No changes here)
  function sanitizeValue(value) {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.startsWith('<') && value.endsWith('>'))) {
      return '';
    }
    return value;
  }

  function sanitizeValueIntType(value) {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.startsWith('<') && value.endsWith('>'))) {
      return 1;
    }
    return value;
  }

  function sanitizeValueToNull(value) {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.startsWith('<') && value.endsWith('>'))) {
      return null;
    }
    return value;
  }

  function validateData({ data, employeeTypes }) {
    let newData = [];

    data.forEach((item) => {
      const missing = [];

      if (!item.employeeId) missing.push('Employee Id');
      if (!item.firstName) missing.push('First Name');
      if (!item.lastName) missing.push('Last Name');
      if (!item.employeeInformation) missing.push('Employee Information');

      const existingEmployee = employees.find(employee => employee.employeeId === item.employeeId?.toString());

      let errorMessage = "";

      if (missing.length > 0) {
        errorMessage += ` Missing fields - ${missing.join(', ')}`;
      }

      if (existingEmployee) {
        errorMessage += errorMessage.length > 0 ? ` EmployeeId already exists.` : `EmployeeId already exists. `;
      }

      // - - - MSC Validation - - -
      const mscCheck = validateMscCompatibility({
        excelMscDescription: item.employeeInformation.maintenanceSalaryCombinationV2,
        salaryType: item.employeeInformation.salaryType,
        row: item.employeeInformation, // Passing the whole item which contains the IDs via SALARY_FILTER_MAP keys
        allRules: options.mscRules,
        employeeTypes: employeeTypes,
        maintenanceTypes: options.maintenanceTypes,
        mscFilters: FEATURES.MAINTENANCE_SALARY_FILTERS
      });

      if (!mscCheck.isValid) {
        errorMessage += `${mscCheck.error} `;
      } else if (item.salaryType === "MaintenanceSalaryCombination") {
        // Map the found ID back to the object for the backend
        item.maintenanceSalaryCombinationV2Id = mscCheck.ruleId;
      }
      // - - - END MSC Validation - - -

      newData.push({ ...item, errors: errorMessage });
    });

    return newData;
  }

  // 3. Return a Promise
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    function isMaintenanceSalaryCombination2(salaryType) {
      const result = (salaryType && salaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION)
      return result;
    }

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const parseWorksheet = (worksheet) => {
          return worksheet ? XLSX.utils.sheet_to_json(worksheet, { defval: '' }) : [];
        };

        // Parse all sheets (always parse these core sheets)
        const employeePersonalInformation = parseWorksheet(workbook.Sheets['EmployeePersonalInformation']);
        const employeeInformation = parseWorksheet(workbook.Sheets['EmployeeInformation']);
        const employeeAccountInformation = parseWorksheet(workbook.Sheets['AccountInformation']);

        // Conditionally parse new sheets based on feature flags
        const employeePassportDetail = parseWorksheet(workbook.Sheets['PassportDetail']);
        const employeeSeamanBookDetail = showSeamanBook ? parseWorksheet(workbook.Sheets['SeamanBookDetail']) : [];
        const employeeLicenseInformation = showLicenseInfo ? parseWorksheet(workbook.Sheets['LicenseInformation']) : [];

        const employeeSalary = parseWorksheet(workbook.Sheets['EmployeeSalary']);
        const employeeBankAccounts = parseWorksheet(workbook.Sheets['BankAccount']);
        const employeeEducationAttainments = parseWorksheet(workbook.Sheets['EducationalAttainment']);
        const employeeTrainingSeminars = parseWorksheet(workbook.Sheets['TrainingAndSeminar']);
        const employeeDependents = parseWorksheet(workbook.Sheets['Dependents']);
        const employeeFamilyData = parseWorksheet(workbook.Sheets['FamilyData']);
        const employeeEmergencyContacts = parseWorksheet(workbook.Sheets['EmergencyContact']);
        const employeeLeaves = parseWorksheet(workbook.Sheets['EmployeeLeaves']);
        const employeeStatus = parseWorksheet(workbook.Sheets['EmployeeStatus']);

        const bulkData = [];
        const processedIds = new Set();

        employeePersonalInformation.forEach(personalInfo => {
          if (!personalInfo.EmployeeId) return;
          if (processedIds.has(personalInfo.EmployeeId)) return;
          processedIds.add(personalInfo.EmployeeId);

          // Filtering Existing
          const filteredEmployeeInformation = employeeInformation.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredAccountInformation = employeeAccountInformation.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredSalary = employeeSalary.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredBankAccount = employeeBankAccounts.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredEducationAttainments = employeeEducationAttainments.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredTrainingAndSeminar = employeeTrainingSeminars.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredDependents = employeeDependents.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredFamilyData = employeeFamilyData.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredEmergencyContacts = employeeEmergencyContacts.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredEmployeeLeaves = employeeLeaves.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredEmployeeStatus = employeeStatus.filter(row => row.EmployeeId === personalInfo.EmployeeId);

          // Conditionally filter new sheets
          const filteredPassport = employeePassportDetail.filter(row => row.EmployeeId === personalInfo.EmployeeId);
          const filteredSeaman = showSeamanBook ? employeeSeamanBookDetail.filter(row => row.EmployeeId === personalInfo.EmployeeId) : [];
          const filteredLicense = showLicenseInfo ? employeeLicenseInformation.filter(row => row.EmployeeId === personalInfo.EmployeeId) : [];

          let foundLocation = null;
          let foundEmployment = null;
          let foundDepartment = null;
          let foundSection = null;
          let foundClassification = null;
          let foundPaytype = null;
          let foundShiftSchedule = null;
          let foundPosition = null;
          let foundCompany = null;
          let foundSalaryType = null;
          let foundMaintenanceSalaryCombination = null; // only fill up when the Salary Type is "MaintenanceSalaryCombination"
          let foundJobTitle = null;
          let foundEmployeeGroup1 = null;
          let foundEmployeeGroup2 = null;
          let foundPlantilya = null;
          let foundCostCenter = null;
          let foundClient = null;
          let foundEmployeeType = null;
          let foundVessel = null;
          let foundDestination = null;

          if (filteredEmployeeInformation.length > 0) {
            const employeeInfo = filteredEmployeeInformation[0];

            foundLocation = dataLocation.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Location)?.toString().toLowerCase());
            foundEmployment = dataEmployment.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Employment)?.toString().toLowerCase());
            foundDepartment = dataDepartment.find(data => (FEATURES.DEPARTMENT_CODE ? data.code?.toLowerCase() : data.description?.toLowerCase()) === sanitizeValue(FEATURES.DEPARTMENT_CODE ? employeeInfo.DepartmentCode : employeeInfo.Department)?.toString().toLowerCase());
            foundSection = dataSection.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Section)?.toString().toLowerCase());
            foundClassification = dataClassification.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Classification)?.toString().toLowerCase());
            foundPaytype = dataPaytype.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.PayType)?.toString().toLowerCase());
            foundShiftSchedule = dataShiftSchedule.find(data => (FEATURES.SHIFTSCHEDULE_CODE ? data.code?.toLowerCase() : data.shiftName?.toLowerCase()) === sanitizeValue(FEATURES.SHIFTSCHEDULE_CODE ? employeeInfo.ShiftScheduleCode : employeeInfo.ShiftSchedule)?.toString().toLowerCase());
            foundPosition = dataPosition.find(data => (FEATURES.POSITION_CODE ? data.code?.toLowerCase() : data.description?.toLowerCase()) === sanitizeValue(FEATURES.POSITION_CODE ? employeeInfo.PositionCode : employeeInfo.Position)?.toString().toLowerCase());
            foundCompany = dataCompany.find(data => data.name?.toLowerCase() === sanitizeValue(employeeInfo.Company)?.toString().toLowerCase());
            foundSalaryType = dataSalaryType.find(data => { let value = sanitizeValue(employeeInfo.SalaryType)?.toString().toLowerCase(); return data.toLowerCase() === value.toLowerCase() });
            foundMaintenanceSalaryCombination = dataForMaintenanceSalaryCombination.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.MaintenanceSalaryCombination)?.toString().toLowerCase());
            foundJobTitle = dataForJobTitle.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.JobTitle)?.toString().toLowerCase());
            foundEmployeeGroup1 = dataForEmployeeGroup1.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.EmployeeGroup1)?.toString().toLowerCase());
            foundEmployeeGroup2 = dataForEmployeeGroup2.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.EmployeeGroup2)?.toString().toLowerCase());
            foundPlantilya = dataForPlantilya.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Plantilya)?.toString().toLowerCase());
            foundCostCenter = dataForCostCenter.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.CostCenter)?.toString().toLowerCase());
            foundClient = dataForClient.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.Client)?.toString().toLowerCase());
            foundEmployeeType = FEATURES.EMPLOYEELIST_EMPLOYEETYPE ? dataForEmployeeType.find(data => data.description?.toLowerCase() === sanitizeValue(employeeInfo.EmployeeType)?.toString().toLowerCase()) : null;
            foundVessel = FEATURES.EMPLOYEELIST_VESSEL ? dataForVessel.find(data => data.code?.toLowerCase() === sanitizeValue(employeeInfo.Vessel)?.toString().toLowerCase()) : null;
            foundDestination = FEATURES.EMPLOYEE_INFORMATION_DESTINATION ? dataForDestination.find(data => data.code?.toLowerCase() === sanitizeValue(employeeInfo.Destination)?.toString().toLowerCase()) : null;
          }

          // Build bank accounts with conditional taxable status
          const processedBankAccounts = filteredBankAccount.map(bank => ({
            employeeId: sanitizeValue(bank.EmployeeId).toString(),
            bankAccount: sanitizeValue(bank.BankAccount).toString(),
            bankName: sanitizeValue(bank.BankName)?.toString(),
            ...(showTaxableStatus && { taxableStatus: sanitizeValue(bank.TaxableStatus)?.toString() }),
          }));

          bulkData.push({
            employeeId: sanitizeValue(personalInfo.EmployeeId).toString().trim(),
            lastName: sanitizeValue(personalInfo.LastName),
            firstName: sanitizeValue(personalInfo.FirstName),
            middleName: sanitizeValue(personalInfo.MiddleName),
            extensionName: sanitizeValue(personalInfo.ExtensionName),
            fullName: sanitizeValue(personalInfo.LastName) + ", " + sanitizeValue(personalInfo.FirstName),
            nickName: sanitizeValue(personalInfo.NickName),
            age: personalInfo.Age ? sanitizeValueToNull(personalInfo.Age.toString()) : null,
            dateOfBirth: personalInfo.DateOfBirth ? convertToDateTime(personalInfo.DateOfBirth) : null,
            placeOfBirth: sanitizeValue(personalInfo.PlaceOfBirth),
            civilStatus: sanitizeValue(personalInfo.CivilStatus),
            bloodType: sanitizeValue(personalInfo.BloodType),
            height: sanitizeValue(personalInfo.Height)?.toString(),
            weight: sanitizeValue(personalInfo.Weight)?.toString(),
            citizenship: sanitizeValue(personalInfo.Citizenship),
            religion: sanitizeValue(personalInfo.Religion),
            gender: sanitizeValue(personalInfo.Gender),
            mobileNumber: sanitizeValue(personalInfo.MobileNumber.toString()),
            emailAddress: sanitizeValue(personalInfo.EmailAddress),
            blkLot: sanitizeValue(personalInfo.BlkLot.toString()),
            province: sanitizeValue(personalInfo.Province.toString()),
            region: sanitizeValue(personalInfo.Region.toString()),
            street: sanitizeValue(personalInfo.Street)?.toString(),
            barangay: sanitizeValue(personalInfo.Barangay)?.toString(),
            city: sanitizeValue(personalInfo.City),
            postalCode: sanitizeValue(personalInfo.PostalCode.toString()),
            yearsOfStay: personalInfo.YearsOfStay ? sanitizeValueToNull(personalInfo.YearsOfStay.toString()) : null,

            employeeInformation: filteredEmployeeInformation[0] ? {
              employeeId: sanitizeValue(filteredEmployeeInformation[0].EmployeeId).toString(),
              location: filteredEmployeeInformation[0].Location ? (foundLocation ? sanitizeValue(filteredEmployeeInformation[0].Location).toString() : "") : "",
              dateEmployed: filteredEmployeeInformation[0].DateEmployed ? convertToDateTime(filteredEmployeeInformation[0].DateEmployed) : null,
              dateRegular: filteredEmployeeInformation[0].DateRegular ? convertToDateTime(filteredEmployeeInformation[0].DateRegular) : null,
              dateProbationary: filteredEmployeeInformation[0].DateProbationary ? convertToDateTime(filteredEmployeeInformation[0].DateProbationary) : null,
              dateResigned: filteredEmployeeInformation[0].DateResigned ? convertToDateTime(filteredEmployeeInformation[0].DateResigned) : null,
              dateProjectBase: filteredEmployeeInformation[0].DateProjectBase ? convertToDateTime(filteredEmployeeInformation[0].DateProjectBase) : null,
              dateContractual: filteredEmployeeInformation[0].DateContractual ? convertToDateTime(filteredEmployeeInformation[0].DateContractual) : null,
              employmentDuration: filteredEmployeeInformation[0].EmploymentDuration ? convertToDateTime(filteredEmployeeInformation[0].EmploymentDuration) : null,
              employment: filteredEmployeeInformation[0].Employment ? (foundEmployment ? sanitizeValue(filteredEmployeeInformation[0].Employment).toString() : "") : "",
              department: (FEATURES.DEPARTMENT_CODE ? filteredEmployeeInformation[0].DepartmentCode : filteredEmployeeInformation[0].Department) ? (foundDepartment ? (FEATURES.DEPARTMENT_CODE ? foundDepartment.code : foundDepartment.description) : "") : "",
              section: filteredEmployeeInformation[0].Section ? (foundSection ? sanitizeValue(filteredEmployeeInformation[0].Section).toString() : "") : "",
              classification: filteredEmployeeInformation[0].Classification ? (foundClassification ? sanitizeValue(filteredEmployeeInformation[0].Classification).toString() : "") : "",
              payType: filteredEmployeeInformation[0].PayType ? (foundPaytype ? sanitizeValue(filteredEmployeeInformation[0].PayType).toString() : "") : "",
              shiftSchedule: (FEATURES.SHIFTSCHEDULE_CODE ? filteredEmployeeInformation[0].ShiftScheduleCode : filteredEmployeeInformation[0].ShiftSchedule) ? (foundShiftSchedule ? (FEATURES.SHIFTSCHEDULE_CODE ? foundShiftSchedule.code : foundShiftSchedule.shiftName) : "") : "",
              position: (FEATURES.POSITION_CODE ? filteredEmployeeInformation[0].PositionCode : filteredEmployeeInformation[0].Position) ? (foundPosition ? (FEATURES.POSITION_CODE ? foundPosition.code : foundPosition.description) : "") : "",
              company: filteredEmployeeInformation[0].Company ? (foundCompany ? sanitizeValue(filteredEmployeeInformation[0].Company).toString() : "") : "",
              salaryType: filteredEmployeeInformation[0].SalaryType ? (foundSalaryType ? sanitizeValue(filteredEmployeeInformation[0].SalaryType).toString() : "") : "",
              // maintenanceSalaryCombinationV2: filteredEmployeeInformation[0].MaintenanceSalaryCombination ? (foundMaintenanceSalaryCombination ? sanitizeValue(filteredEmployeeInformation[0].MaintenanceSalaryCombination).toString() : "") : "",
              maintenanceSalaryCombinationV2:
                isMaintenanceSalaryCombination2(filteredEmployeeInformation[0].SalaryType)
                  ? (
                    filteredEmployeeInformation[0].MaintenanceSalaryCombination
                      ? (
                        foundMaintenanceSalaryCombination
                          ? sanitizeValue(
                            filteredEmployeeInformation[0].MaintenanceSalaryCombination
                          ).toString()
                          : ""
                      )
                      : ""
                  )
                  : null,
              jobTitle: filteredEmployeeInformation[0].JobTitle ? (foundJobTitle ? sanitizeValue(filteredEmployeeInformation[0].JobTitle).toString() : "") : "",
              employeeGroup1: filteredEmployeeInformation[0].EmployeeGroup1 ? (foundEmployeeGroup1 ? sanitizeValue(filteredEmployeeInformation[0].EmployeeGroup1).toString() : "") : "",
              employeeGroup2: filteredEmployeeInformation[0].EmployeeGroup2 ? (foundEmployeeGroup2 ? sanitizeValue(filteredEmployeeInformation[0].EmployeeGroup2).toString() : "") : "",
              plantilya: filteredEmployeeInformation[0].Plantilya ? (foundPlantilya ? sanitizeValue(filteredEmployeeInformation[0].Plantilya).toString() : "") : "",
              costCenter: filteredEmployeeInformation[0].CostCenter ? (foundCostCenter ? sanitizeValue(filteredEmployeeInformation[0].CostCenter).toString() : "") : "",
              client: filteredEmployeeInformation[0].Client ? (foundClient ? sanitizeValue(filteredEmployeeInformation[0].Client).toString() : "") : "",
              employeeType: filteredEmployeeInformation[0].EmployeeType ? (foundEmployeeType ? foundEmployeeType.description : sanitizeValue(filteredEmployeeInformation[0].EmployeeType).toString()) : "",
              vessel: filteredEmployeeInformation[0].Vessel ? (foundVessel ? foundVessel.code : sanitizeValue(filteredEmployeeInformation[0].Vessel).toString()) : "",
              destination: filteredEmployeeInformation[0].Destination ? (foundDestination ? foundDestination.code : sanitizeValue(filteredEmployeeInformation[0].Destination).toString()) : "",

              // These are required by validateMscCompatibility
              locationId: foundLocation?.id ?? null,
              employmentId: foundEmployment?.id ?? null,
              departmentId: foundDepartment?.id ?? null,
              sectionId: foundSection?.id ?? null,
              classificationId: foundClassification?.id ?? null,
              payTypeId: foundPaytype?.id ?? null,
              shiftScheduleId: foundShiftSchedule?.id ?? null,
              positionId: foundPosition?.id ?? null,
              companyId: foundCompany?.id ?? null,
              jobTitleId: foundJobTitle?.id ?? null,
              employeeGroup1Id: foundEmployeeGroup1?.id ?? null,
              employeeGroup2Id: foundEmployeeGroup2?.id ?? null,
              plantilyaId: foundPlantilya?.id ?? null,
              costCenterId: foundCostCenter?.id ?? null,
              clientId: foundClient?.id ?? null,
              employeeTypeId: foundEmployeeType?.id ?? null,
              vesselId: foundVessel?.id ?? null,
              destinationId: foundDestination?.id ?? null,
              // --- end ---

              createdDate: new Date(),
              createdBy: 0,
            } : null,

            employeeAccountInformation: filteredAccountInformation[0] ? {
              employeeId: sanitizeValue(filteredAccountInformation[0].EmployeeId).toString(),
              sss: sanitizeValue(filteredAccountInformation[0].Sss).toString(),
              tin: sanitizeValue(filteredAccountInformation[0].Tin).toString(),
              philhealth: sanitizeValue(filteredAccountInformation[0].Philhealth).toString(),
              pagibig: sanitizeValue(filteredAccountInformation[0].Pagibig).toString(),
            } : null,

            // Conditionally include new sheets only if their features are enabled
            employeePassportDetail: filteredPassport[0] ? {
              employeeId: sanitizeValue(filteredPassport[0].EmployeeId).toString(),
              passportNumber: sanitizeValue(filteredPassport[0].PassportNumber).toString(),
              passportIssueDate: filteredPassport[0].PassportIssueDate ? convertToDateTime(filteredPassport[0].PassportIssueDate) : null,
              passportExpiryDate: filteredPassport[0].PassportExpiryDate ? convertToDateTime(filteredPassport[0].PassportExpiryDate) : null,
            } : null,

            ...(showSeamanBook && {
              employeeSeamanBookDetail: filteredSeaman[0] ? {
                employeeId: sanitizeValue(filteredSeaman[0].EmployeeId).toString(),
                seamanBookNumber: sanitizeValue(filteredSeaman[0].SeamanBookNumber).toString(),
                bookIssueDate: filteredSeaman[0].BookIssueDate ? convertToDateTime(filteredSeaman[0].BookIssueDate) : null,
                bookExpiryDate: filteredSeaman[0].BookExpiryDate ? convertToDateTime(filteredSeaman[0].BookExpiryDate) : null,
              } : null,
            }),

            ...(showLicenseInfo && {
              employeeLicenseInformation: filteredLicense[0] ? {
                employeeId: sanitizeValue(filteredLicense[0].EmployeeId).toString(),
                licenseCode: sanitizeValue(filteredLicense[0].LicenseCode).toString(),
                licenseNumber: sanitizeValue(filteredLicense[0].LicenseNumber).toString(),
                description: sanitizeValue(filteredLicense[0].Description).toString(),
                expirationDate: filteredLicense[0].ExpirationDate ? convertToDateTime(filteredLicense[0].ExpirationDate) : null,
                isQualified: sanitizeValue(filteredLicense[0].IsQualified).toString().toLowerCase() === 'yes',
              } : null,
            }),

            employeeBankAccounts: processedBankAccounts,

            employeeSalary: filteredSalary[0] ? {
              employeeId: sanitizeValue(filteredSalary[0].EmployeeId).toString(),
              salary: sanitizeValueIntType(filteredSalary[0].Salary).toString(),
              allowance: sanitizeValueIntType(filteredSalary[0].Allowance).toString()
            } : null,

            employeeEducationAttainments: filteredEducationAttainments ? filteredEducationAttainments.map(data => ({
              employeeId: sanitizeValue(data.EmployeeId).toString(),
              level: sanitizeValue(data.Level),
              schoolName: sanitizeValue(data.SchoolName),
              address: sanitizeValue(data.Address),
              dateFrom: data.DateFrom ? convertToDateTime(data.DateFrom) : null,
              dateTo: data.DateTo ? convertToDateTime(data.DateTo) : null,
              course: sanitizeValue(data.Course),
              honors: sanitizeValue(data.Honors),
            })) : [],

            employeeTrainingSeminars: filteredTrainingAndSeminar ? filteredTrainingAndSeminar.map(data => ({
              employeeId: sanitizeValue(data.EmployeeId).toString(),
              title: sanitizeValue(data.Title),
              dateFrom: data.DateFrom ? convertToDateTime(data.DateFrom) : null,
              dateTo: data.DateTo ? convertToDateTime(data.DateTo) : null,
            })) : [],

            employeeDependents: filteredDependents ? filteredDependents.map(data => ({
              employeeId: sanitizeValue(data.EmployeeId).toString(),
              lastName: sanitizeValue(data.LastName),
              firstName: sanitizeValue(data.FirstName),
              middleName: sanitizeValue(data.MiddleName),
              extensionName: sanitizeValue(data.ExtensionName),
              dateOfBirth: data.DateOfBirth ? convertToDateTime(data.DateOfBirth) : null,
              gender: sanitizeValue(data.Gender),
              civilStatus: sanitizeValue(data.CivilStatus),
            })) : [],

            employeeFamilyData: filteredFamilyData ? filteredFamilyData.map(data => ({
              employeeId: sanitizeValue(data.EmployeeId).toString(),
              name: sanitizeValue(data.Name),
              dateOfBirth: data.DateOfBirth ? convertToDateTime(data.DateOfBirth) : null,
              occupation: sanitizeValue(data.Occupation),
              contactNumber: sanitizeValue(data.ContactNumber).toString(),
              age: sanitizeValueIntType(data.Age),
              address: sanitizeValue(data.Address),
              relationship: sanitizeValue(data.Relationship),
            })) : [],

            employeeEmergencyContacts: filteredEmergencyContacts ? filteredEmergencyContacts.map(data => ({
              employeeId: sanitizeValue(data.EmployeeId).toString(),
              name: sanitizeValue(data.Name),
              relationship: sanitizeValue(data.Relationship),
              contactNumber: sanitizeValue(data.ContactNumber).toString(),
              address: sanitizeValue(data.Address),
            })) : [],

            // REPLACED: Hardcoded leaves with dynamic balance array
            employeeLeaveBalances: filteredEmployeeLeaves[0] ? activeLeaveTypes.map(type => {
              const row = filteredEmployeeLeaves[0];
              // Keys match the labels used in the download template
              const totalKey = `Total ${type.description}`;
              const remainingKey = `${type.description} Remaining`;

              return {
                leaveTypeId: type.id,
                totalLeaveCredit: sanitizeValue(row[totalKey]) !== '' ? parseFloat(sanitizeValue(row[totalKey])) : null,
                remainingLeaveCredit: sanitizeValue(row[remainingKey]) !== '' ? parseFloat(sanitizeValue(row[remainingKey])) : null
              };
            }).filter(leave => leave.totalLeaveCredit !== null || leave.remainingLeaveCredit !== null) : [],

            employeeStatuses: filteredEmployeeStatus[0] ? {
              employeeId: sanitizeValue(filteredEmployeeStatus[0].EmployeeId).toString(),
              status: filteredEmployeeStatus[0].EmployeeStatus ? sanitizeValue(filteredEmployeeStatus[0].EmployeeStatus) : "Active",
            } : null,
          });
        });

        // Validate and Sort
        const validatedData = validateData({ data: bulkData, employeeTypes: dataForEmployeeType });
        const sortedData = validatedData.sort((a, b) => {
          const hasErrorA = a.errors && a.errors.length > 0;
          const hasErrorB = b.errors && b.errors.length > 0;
          return hasErrorB - hasErrorA;
        });

        resolve(sortedData);

      } catch (err) {
        console.error("Error processing Excel file:", err);
        reject(err);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

// Processing for Upload Employee/s Information
export const processBulkUpdateFile = ({ file, employees, options }) => {
  console.log("Using the bulk.. Employee Information Upload");
  return new Promise((resolve, reject) => {

    // · · · Helpers · · ·
    function sanitizeValue(value) {
      const skipValues = ['-', '`', 'n/a', 'na', 'none', '.'];

      if (value === null || value === undefined || value === '') return null;

      const strValue = value.toString().trim();

      if (strValue.startsWith('<') && strValue.endsWith('>')) return null;
      if (skipValues.includes(strValue.toLowerCase())) return null;

      return strValue;
    }

    function validateData({ data, employeeTypes }) {
      return data.map(item => {
        const missing = [];
        let errorMessage = "";

        if (!item.employeeId) missing.push('Employee Id');

        const existingEmployee = employees.find(
          employee => employee.employeeId === item.employeeId?.toString()
        );

        if (!existingEmployee) {
          errorMessage += `Employee Id does not exist. `;
        }

        if (missing.length > 0) {
          errorMessage += `Missing fields - ${missing.join(', ')}`;
        }

        // - - - MSC Validation - - -
        const mscCheck = validateMscCompatibility({
          excelMscDescription: item.maintenanceSalaryCombinationV2,
          salaryType: item.salaryType,
          row: item, // Passing the whole item which contains the IDs via SALARY_FILTER_MAP keys
          allRules: options.mscRules,
          employeeTypes: employeeTypes,
          maintenanceTypes: options.maintenanceTypes,
          mscFilters: FEATURES.MAINTENANCE_SALARY_FILTERS
        });

        if (!mscCheck.isValid) {
          errorMessage += `${mscCheck.error} `;
        } else if (item.salaryType === "MaintenanceSalaryCombination") {
          // Map the found ID back to the object for the backend
          item.maintenanceSalaryCombinationV2Id = mscCheck.ruleId;
        }
        // - - - END MSC Validation - - -


        return { ...item, errors: errorMessage };
      });
    }

    // · · · Read File · · ·
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        // · · · Fetch Reference Data · · ·
        const [
          dataLocation,
          dataEmployment,
          dataDepartment,
          dataSection,
          dataClassification,
          dataPosition,
          dataPaytype,
          dataShiftSchedule,
          dataCompany,
          dataForJobTitle,
          dataForEmployeeGroup1,
          dataForEmployeeGroup2,
          dataForPlantilya,
          dataForCostCenter,
          dataForClient,
          dataForEmployeeType,
          dataForVessel,
          dataForDestination,
        ] = await Promise.all([
          fetchDataForLocation(),
          fetchDataForEmployment(),
          fetchDataForDepartment(),
          fetchDataForSection(),
          fetchDataForClassification(),
          fetchDataForPosition(),
          fetchDataForPaytype(),
          fetchDataForShiftSchedule(),
          fetchDataForCompany(),
          fetchDataForJobTitle(),
          fetchDataForEmployeeGroup1(),
          fetchDataForEmployeeGroup2(),
          fetchDataForPlantilya(),
          fetchDataForCostCenter(),
          fetchDataForClient(),
          fetchDataForEmployeeType(),
          fetchDataForVessel(),
          fetchDataForDestination(),
        ]);

        const dataSalaryType = fetchSalaryType(); // sync, no await needed
        const dataForMaintenanceSalaryCombination = options?.mscRules ?? [];

        // · · · Parse Worksheet · · ·
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const employeeInformation = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]],
          { defval: '' }
        );

        // · · · Map Rows · · ·
        const bulkData = employeeInformation.map(personalInfo => {
          const info = personalInfo;

          const foundLocation = dataLocation.find(d => d.description?.toLowerCase() === sanitizeValue(info.Location)?.toString().toLowerCase());
          const foundEmployment = dataEmployment.find(d => d.description?.toLowerCase() === sanitizeValue(info.Employment)?.toString().toLowerCase());
          // const foundDepartment = dataDepartment.find(d => d.description?.toLowerCase() === sanitizeValue(info.Department)?.toString().toLowerCase());
          const foundDepartment = dataDepartment.find(d => (FEATURES.DEPARTMENT_CODE ? d.code?.toLowerCase() : d.description?.toLowerCase()) === sanitizeValue(FEATURES.DEPARTMENT_CODE ? info.DepartmentCode : info.Department)?.toString().toLowerCase());
          const foundSection = dataSection.find(d => d.description?.toLowerCase() === sanitizeValue(info.Section)?.toString().toLowerCase());
          const foundClassification = dataClassification.find(d => d.description?.toLowerCase() === sanitizeValue(info.Classification)?.toString().toLowerCase());
          // const foundPosition = dataPosition.find(d => d.description?.toLowerCase() === sanitizeValue(info.Position)?.toString().toLowerCase());
          const foundPosition = dataPosition.find(d => (FEATURES.POSITION_CODE ? d.code?.toLowerCase() : d.description?.toLowerCase()) === sanitizeValue(FEATURES.POSITION_CODE ? info.PositionCode : info.Position)?.toString().toLowerCase());
          const foundPaytype = dataPaytype.find(d => d.description?.toLowerCase() === sanitizeValue(info.PayType)?.toString().toLowerCase());
          const foundShiftSchedule = dataShiftSchedule.find(d => d.shiftName?.toLowerCase() === sanitizeValue(info.ShiftSchedule)?.toString().toLowerCase());
          const foundCompany = dataCompany.find(d => d.name?.toLowerCase() === sanitizeValue(info.Company)?.toString().toLowerCase());
          const foundSalaryType = dataSalaryType.find(d => d.toLowerCase() === sanitizeValue(info.SalaryType)?.toString().toLowerCase());
          const foundMaintenanceSalaryCombination = dataForMaintenanceSalaryCombination.find(d => d.description?.toLowerCase() === sanitizeValue(info.MaintenanceSalaryCombination)?.toString().toLowerCase());
          const foundJobTitle = dataForJobTitle.find(d => d.description?.toLowerCase() === sanitizeValue(info.JobTitle)?.toString().toLowerCase());
          const foundEmployeeGroup1 = dataForEmployeeGroup1.find(d => d.description?.toLowerCase() === sanitizeValue(info.EmployeeGroup1)?.toString().toLowerCase());
          const foundEmployeeGroup2 = dataForEmployeeGroup2.find(d => d.description?.toLowerCase() === sanitizeValue(info.EmployeeGroup2)?.toString().toLowerCase());
          const foundPlantilya = dataForPlantilya.find(d => d.description?.toLowerCase() === sanitizeValue(info.Plantilya)?.toString().toLowerCase());
          const foundCostCenter = dataForCostCenter.find(d => d.description?.toLowerCase() === sanitizeValue(info.CostCenter)?.toString().toLowerCase());
          const foundClient = dataForClient.find(d => d.description?.toLowerCase() === sanitizeValue(info.Client)?.toString().toLowerCase());
          const foundEmployeeType = FEATURES.EMPLOYEELIST_EMPLOYEETYPE ? dataForEmployeeType.find(data => data.description?.toLowerCase() === sanitizeValue(info.EmployeeType)?.toString().toLowerCase()) : null;
          const foundVessel = dataForVessel.find(d => d.code?.toLowerCase() === sanitizeValue(info.Vessel)?.toString().toLowerCase());
          const foundDestination = dataForDestination.find(d => d.code?.toLowerCase() === sanitizeValue(info.Destination)?.toString().toLowerCase());

          const isMaintenanceSalaryCombination = foundSalaryType && foundSalaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION;

          return {
            employeeId: personalInfo.EmployeeId.toString(),
            fullName: personalInfo.EmployeeName.toString(),
            locationId: foundLocation?.id ?? null,
            location: personalInfo.Location,
            employmentId: foundEmployment?.id ?? null,
            employment: personalInfo.Employment,
            departmentId: foundDepartment?.id ?? null,
            department: FEATURES.DEPARTMENT_CODE ? personalInfo.DepartmentCode : personalInfo.Department,
            sectionId: foundSection?.id ?? null,
            section: personalInfo.Section,
            classificationId: foundClassification?.id ?? null,
            classification: personalInfo.Classification,
            payTypeId: foundPaytype?.id ?? null,
            payType: personalInfo.PayType,
            shiftScheduleId: foundShiftSchedule?.id ?? null,
            shiftSchedule: personalInfo.ShiftSchedule,
            positionId: foundPosition?.id ?? null,
            position: FEATURES.POSITION_CODE ? info.PositionCode : info.Position,
            salaryType: foundSalaryType ?? null,
            maintenanceSalaryCombinationV2Id: isMaintenanceSalaryCombination ? (foundMaintenanceSalaryCombination?.id ?? null) : null,
            maintenanceSalaryCombinationV2: isMaintenanceSalaryCombination ? personalInfo.MaintenanceSalaryCombination : null,
            companyId: foundCompany?.id ?? null,
            company: personalInfo.Company,
            jobTitleId: foundJobTitle?.id ?? null,
            jobTitle: personalInfo.JobTitle,
            employeeGroup1Id: foundEmployeeGroup1?.id ?? null,
            employeeGroup1: personalInfo.EmployeeGroup1,
            employeeGroup2Id: foundEmployeeGroup2?.id ?? null,
            employeeGroup2: personalInfo.EmployeeGroup2,
            plantilyaId: foundPlantilya?.id ?? null,
            plantilya: personalInfo.Plantilya,
            costCenterId: foundCostCenter?.id ?? null,
            costCenter: personalInfo.CostCenter,
            clientId: foundClient?.id ?? null,
            client: personalInfo.Client,
            employeeTypeId: foundEmployeeType?.id ?? null,
            employeeType: personalInfo.EmployeeType,
            vesselId: foundVessel?.id ?? null,
            vessel: personalInfo.Vessel,
            destinationId: foundDestination?.id ?? null,
            destination: personalInfo.Destination,
          };
        });

        // · · · Validate & Sort · · ·
        const validated = validateData({ data: bulkData, employeeTypes: dataForEmployeeType });
        const sorted = validated.sort((a, b) => {
          const hasErrorA = a.errors && a.errors.length > 0;
          const hasErrorB = b.errors && b.errors.length > 0;
          return hasErrorB - hasErrorA;
        });

        resolve(sorted);

      } catch (err) {
        console.error('[processBulkUpdateFile] Error:', err);
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};

// MODIFIED: Function is now async to fetch dynamic leave types from DB
export const processLeaveFile = async (file, employees) => {
  // 1. Fetch active leave types to know which columns to look for in Excel
  const activeLeaveTypesRes = await leaveTypeService.getActive();
  const activeLeaveTypes = activeLeaveTypesRes.data || [];

  return new Promise((resolve, reject) => {
    // · · · Helpers · · ·
    function sanitizeValue(value) {
      if (value === null || value === undefined || value === '' ||
        (typeof value === 'string' && value.startsWith('<') && value.endsWith('>'))) {
        return ""; // Changed to empty string to detect "no value" vs "0"
      }
      return value;
    }

    function sanitizeValueString(value) {
      if (value === null || value === undefined || value === '' ||
        (typeof value === 'string' && value.startsWith('<') && value.endsWith('>'))) {
        return "";
      }
      return value;
    }

    function validateData(data) {
      return data.map(item => {
        const missing = [];
        let errorMessage = "";

        if (!item.employeeId) missing.push('Employee Id');

        const existingEmployee = employees.find(
          employee => employee.employeeId === item.employeeId?.toString()
        );

        if (!existingEmployee) {
          errorMessage += `Employee Id does not exist. `;
        }

        if (missing.length > 0) {
          errorMessage += `Missing fields - ${missing.join(', ')}`;
        }

        return { ...item, errors: errorMessage };
      });
    }

    // · · · Read File · · ·
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const employeeInformation = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]],
          { defval: '' }
        );

        // · · · Map Rows · · ·
        const bulkData = employeeInformation.map(personalInfo => {

          // Helper to find Excel keys regardless of extra spaces or casing
          const getExcelValue = (targetLabel) => {
            const key = Object.keys(personalInfo).find(k =>
              k.trim().toLowerCase() === targetLabel.toLowerCase()
            );
            return key ? personalInfo[key] : null;
          };

          const balances = [];
          const uiFlattened = {};

          // Dynamically map every active leave type
          activeLeaveTypes.forEach(type => {
            const totalLabel = `Total ${type.description}`;
            const remainingLabel = `${type.description} Remaining`;

            const rawTotal = getExcelValue(totalLabel);
            const rawRemaining = getExcelValue(remainingLabel);

            // Only add to payload if the column exists in Excel and has a value
            if (rawTotal !== null || rawRemaining !== null) {
              const totalVal = sanitizeValue(rawTotal) !== '' ? parseFloat(sanitizeValue(rawTotal)) : 0;
              const remainingVal = sanitizeValue(rawRemaining) !== '' ? parseFloat(sanitizeValue(rawRemaining)) : 0;

              // Data for Backend
              balances.push({
                leaveTypeId: type.id,
                totalLeaveCredit: totalVal,
                remainingLeaveCredit: remainingVal
              });

              // Flattened data for UI Table Accessors (total_1, remaining_1, etc.)
              uiFlattened[`total_${type.id}`] = totalVal;
              uiFlattened[`remaining_${type.id}`] = remainingVal;
            }
          });

          return {
            employeeId: sanitizeValueString(getExcelValue('EmployeeId'))?.toString(),
            fullName: sanitizeValueString(getExcelValue('EmployeeName'))?.toString(),
            employeeLeaveBalances: balances, // For API
            errors: "",                     // Placeholder for validation
            ...uiFlattened                  // For UI Table Preview
          };
        });

        // · · · Validate & Sort · · ·
        const validated = validateData(bulkData);
        const sorted = validated.sort((a, b) => {
          const hasErrorA = a.errors && a.errors.length > 0;
          const hasErrorB = b.errors && b.errors.length > 0;
          return hasErrorB - hasErrorA;
        });

        resolve(sorted);

      } catch (err) {
        console.error('[processLeaveFile] Error:', err);
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};

// Processing for Upload Employee/s Address
export const processAddressFile = (file, employees) => {
  console.log("Using the bulk.. Employee Information Upload");
  return new Promise((resolve, reject) => {

    // · · · Helpers · · ·
    function sanitizeValue(value) {
      const skipValues = ['-', '`', 'n/a', 'na', 'none', '.'];

      if (value === null || value === undefined || value === '') return null;

      const strValue = value.toString().trim();

      if (strValue.startsWith('<') && strValue.endsWith('>')) return null;
      if (skipValues.includes(strValue.toLowerCase())) return null;

      return strValue;
    }

    function validateData(data) {
      return data.map(item => {
        const missing = [];
        let errorMessage = "";

        if (!item.employeeId) missing.push('Employee Id');

        const existingEmployee = employees.find(
          employee => employee.employeeId === item.employeeId?.toString()
        );

        if (!existingEmployee) {
          errorMessage += `Employee Id does not exist. `;
        }

        if (missing.length > 0) {
          errorMessage += `Missing fields - ${missing.join(', ')}`;
        }

        return { ...item, errors: errorMessage };
      });
    }

    // · · · Read File · · ·
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        // · · · Parse Worksheet · · ·
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const employeeInformation = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]],
          { defval: '' }
        );

        // · · · Map Rows · · ·
        const bulkData = employeeInformation.map(info => {
          return {
            employeeId: info.EmployeeId.toString(),
            employeeName: info.EmployeeName.toString(),
            region: info.Region?.toString(),
            province: info.Province?.toString(),
            city: info.City?.toString(),
            barangay: info.Barangay?.toString(),
            street: info.Street?.toString(),
            blkLot: info.BlkLot?.toString(),
            postalCode: info.PostalCode?.toString(),
          };
        });

        // · · · Validate & Sort · · ·
        const validated = validateData(bulkData);
        const sorted = validated.sort((a, b) => {
          const hasErrorA = a.errors && a.errors.length > 0;
          const hasErrorB = b.errors && b.errors.length > 0;
          return hasErrorB - hasErrorA;
        });

        console.log({sorted})
        resolve(sorted);

      } catch (err) {
        console.error('[processBulkUpdateFile] Error:', err);
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};
